import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AlertTriangle, CheckCircle, Eye, EyeOff, Lock } from 'lucide-react';
import logger from '../../utils/logger';

const ValidateAccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [isValidating, setIsValidating] = useState(true);
  const [isValid, setIsValid] = useState(false);
  const [isExpired, setIsExpired] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const [isMounted, setIsMounted] = useState(true);

  // Navigation sécurisée avec cleanup
  const safeNavigate = useCallback((path, options = {}) => {
    if (isMounted) {
      navigate(path, options);
    }
  }, [navigate, isMounted]);

  useEffect(() => {
    // Cleanup function pour éviter les erreurs DOM
    return () => {
      setIsMounted(false);
    };
  }, []);

  useEffect(() => {
    const token = searchParams.get('token');
    const expires = searchParams.get('expires');

    // Vérifier la validité du token et de l'expiration
    setTimeout(() => {
      if (!token || !expires) {
        setIsValid(false);
        setIsValidating(false);
        return;
      }

      const expiryTime = parseInt(expires);
      const currentTime = new Date().getTime();

      if (currentTime > expiryTime) {
        setIsExpired(true);
        setIsValid(false);
      } else {
        setIsValid(true);
      }
      
      setIsValidating(false);
    }, 2000);
  }, [searchParams]);

  const validatePassword = (pass) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(pass);
    const hasLowerCase = /[a-z]/.test(pass);
    const hasNumbers = /\d/.test(pass);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(pass);

    return pass.length >= minLength && hasUpperCase && hasLowerCase && hasNumbers && hasSpecialChar;
  };

  const handleCreateAccount = async () => {
    setError('');

    if (!password || !confirmPassword) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    if (!validatePassword(password)) {
      setError('Le mot de passe doit contenir au moins 8 caractères, une majuscule, une minuscule, un chiffre et un caractère spécial');
      return;
    }

    setIsCreatingAccount(true);

    try {
      const token = searchParams.get('token');
      const email = searchParams.get('email'); // Récupérer l'email depuis l'URL
      
      // Appel API simple avec email si disponible
      const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;
      const payload = {
        token: token,
        password: password,
        confirm_password: confirmPassword
      };
      
      // Ajouter l'email s'il est présent dans l'URL
      if (email) {
        payload.email = decodeURIComponent(email);
      }
      
      const response = await fetch(`${API_BASE_URL}/api/auth/activate-collaborator`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.detail || 'Erreur lors de la création du compte');
      }

      // Message simple et redirection immédiate
      alert(`✅ Compte créé avec succès !\nEmail: ${result.email}\nVous pouvez maintenant vous connecter avec cet email.`);
      
      // Redirection simple et directe
      window.location.href = '/login';
      
    } catch (error) {
      logger.error('Erreur création compte:', error);
      setError(error.message || 'Erreur lors de la création du compte. Veuillez réessayer.');
    } finally {
      setIsCreatingAccount(false);
    }
  };

  if (isValidating) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <div className="animate-spin w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full mx-auto mb-4"></div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Validation du lien...</h2>
          <p className="text-slate-600">Vérification de votre invitation en cours</p>
        </div>
      </div>
    );
  }

  if (!isValid || isExpired) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertTriangle size={32} className="text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            {isExpired ? 'Lien expiré' : 'Lien invalide'}
          </h2>
          <p className="text-slate-600 mb-6">
            {isExpired 
              ? 'Ce lien d\'activation a expiré. Les invitations sont valides pendant 15 jours seulement.'
              : 'Ce lien d\'activation n\'est pas valide ou a déjà été utilisé.'
            }
          </p>
          <div className="bg-amber-50 rounded-lg p-4 mb-6">
            <p className="text-amber-800 text-sm">
              <strong>Que faire ?</strong><br/>
              Contactez la personne qui vous a invité pour qu'elle génère un nouveau lien d'activation.
            </p>
          </div>
          <button
            onClick={() => safeNavigate('/login')}
            className="w-full py-3 bg-slate-600 text-white rounded-lg hover:bg-slate-700 transition-colors"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-emerald-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle size={32} className="text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-slate-900 mb-2">Activation de votre accès</h2>
          <p className="text-slate-600">Créez votre mot de passe pour accéder à l'espace client</p>
        </div>

        <form onSubmit={(e) => e.preventDefault()} className="space-y-4">
          {/* Nouveau mot de passe */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Nouveau mot de passe *
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Créez un mot de passe sécurisé"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              8 caractères min, majuscule, minuscule, chiffre et caractère spécial
            </p>
          </div>

          {/* Confirmation mot de passe */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Confirmer le mot de passe *
            </label>
            <div className="relative">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 pr-12 border border-slate-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent"
                placeholder="Confirmez votre mot de passe"
              />
              <button
                type="button"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <p className="text-red-800 text-sm flex items-center">
                <AlertTriangle size={16} className="mr-2" />
                {error}
              </p>
            </div>
          )}

          <button
            onClick={handleCreateAccount}
            disabled={isCreatingAccount}
            className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transition-all flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isCreatingAccount ? (
              <>
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                <span>Création en cours...</span>
              </>
            ) : (
              <>
                <Lock size={16} />
                <span>Créer mon compte</span>
              </>
            )}
          </button>
        </form>

        <div className="mt-6 p-4 bg-slate-50 rounded-xl">
          <p className="text-slate-600 text-sm">
            <strong>Note de sécurité :</strong><br/>
            Votre mot de passe doit être unique et sécurisé. Ne le partagez avec personne.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ValidateAccess;